import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import type { RoadmapMilestone } from '../../../../api/strategy-client/strategy-client.type';
import { formatDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { primaryBase } from '../../../../styles/design-tokens';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getStatusColor } from '../../components';
import {
  getMilestoneStateText,
  getMilestoneStatusText,
} from '../../initiative-performance/milestones-tab/milestone.type';

interface MilestoneLineProps {
  milestone: RoadmapMilestone;
  leftPercent: number;
}

export function MilestoneLine({ milestone, leftPercent }: MilestoneLineProps) {
  const { t } = useTranslation();
  const milestoneStateText = useMemo(() => getMilestoneStateText(t), [t]);
  const milestoneStatusText = useMemo(() => getMilestoneStatusText(t), [t]);

  const toolTipText = (
    <Stack gap={2}>
      <Text size="medium" weight="bold">
        {milestone.name}
      </Text>
      {milestone.description && (
        <Text size="small" weight="medium">
          {milestone.description}
        </Text>
      )}
      <Group justify="space-between">
        <Text size="small">{t('strategy.epics.due_date')}:</Text>
        <Text size="small" weight="medium">
          {formatDate(milestone.target_end_date)}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text size="small">{t('common.status.status')}:</Text>
        <Text size="small" weight="medium">
          {milestoneStatusText[milestone.status] ?? t('strategy.initiatives.statuses.unknown')}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text size="small">{t('strategy.filters.state')}:</Text>
        <Text size="small" weight="medium">
          {milestoneStateText[milestone.state] ?? t('strategy.initiatives.statuses.unknown')}
        </Text>
      </Group>
    </Stack>
  );

  return (
    <MilestoneLineContainer style={{ left: `${leftPercent}%` }}>
      <Tooltip variant="black" label={toolTipText} multiline style={{ maxWidth: 300 }}>
        <MilestoneLabel>
          <Group gap="xs" align="center">
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: 8,
                backgroundColor: getStatusColor(milestone.status ?? InitiativeStatus.Unknown),
              }}
            />
            {milestone.name}
          </Group>
        </MilestoneLabel>
      </Tooltip>
      <MilestoneLineStyled title={`${milestone.name} - ${milestone.target_end_date}`} />
    </MilestoneLineContainer>
  );
}

// Styled components
const MilestoneLineContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 2;
  transform: translateX(-50%);
`;

const MilestoneLabel = styled.div`
  background: white;
  color: ${primaryBase};
  padding: 4px 8px;
  border-radius: 3px;
  border: 2px solid ${primaryBase};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
`;

const MilestoneLineStyled = styled.div`
  width: 2px;
  flex: 1;
  background: ${primaryBase};
  pointer-events: none;
`;
